import { DatePicker, FormInstance, Input, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import SearchMultiplePopup, { SearchMultiplePopupRef } from 'pages/synchPO/SearchMultiplePopup';
import { DATE_FORMAT_DEFAULT } from 'utils/constants';
import { dropdownStatus } from 'utils/dropdown';
import {
  commonMasterDataPresenter,
  commonMasterDataStore,
  commonPresenter,
  commonStore,
} from 'stores/root_store';
import { getAccountId, getFullName, getRole, getUserName } from 'utils/localStorageHelper';
import dayjs from 'dayjs';
import { initSearchFilters } from '../constants';

/**
 * Props cho component form tìm kiếm
 */
interface SearchFormProps {
  form: FormInstance;
  openDrawer: boolean;
  onClose: () => void;
  onFinish: (values: any) => void;
  onReset: () => void;
  disableDeliveryDate: boolean;
  onDeliveryPresetChange: (value: string) => void;
}

/**
 * Component form tìm kiếm cho Kế hoạch bàn giao chất thải/phế liệu
 */
export const WasteHandoverPlanSearchForm = ({
  form,
  openDrawer,
  onClose,
  onFinish,
  onReset,
  disableDeliveryDate,
  onDeliveryPresetChange,
}: SearchFormProps) => {
  const { t } = useTranslation();
  const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string }[]>([]);
  const [creatorOptions, setCreatorOptions] = useState<{ label: string; value: string }[]>([]);
  const [openProductCodeModal, setOpenProductCodeModal] = useState(false);
  const [openVendorModal, setOpenVendorModal] = useState(false);
  const productCodePopupRef = useRef<SearchMultiplePopupRef>(null);
  const vendorPopupRef = useRef<SearchMultiplePopupRef>(null);

  // Load dữ liệu dropdown khi component mount
  useEffect(() => {
    const initDropdowns = async () => {
      try {
        await Promise.allSettled([
          commonPresenter.getListCommonDate(commonStore, 'CommonDate2'),
          commonPresenter.getSeleOrgByAccountId(commonStore, getAccountId()),
        ]);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };
    initDropdowns();
  }, []);

  // Load danh sách phân nhóm chất thải/phế liệu (HĐ) - lấy tất cả, không phân biệt trạng thái
  useEffect(() => {
    const loadWasteGroups = async () => {
      try {
        if (!commonMasterDataStore.wasteGroupingContractOptions.length) {
          await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, false);
        }
        setWasteGroupOptions(
          (commonMasterDataStore.wasteGroupingContractOptions || []).map((item) => ({
            label: item.value,
            value: item.key,
          }))
        );
      } catch (error) {
        console.error('Error loading waste group options:', error);
      }
    };
    loadWasteGroups();
  }, []);

  // Load dropdown người tạo (user BGA_CT)
  useEffect(() => {
    const loadCreators = async () => {
      try {
        const users = await commonPresenter.getDropdownUserBGA_CT();
        setCreatorOptions(
          (users || []).map((item) => ({
            label: item.value, // tên hiển thị
            value: item.key,   // mã user
          }))
        );
      } catch (error) {
        console.error('Error loading creator options:', error);
      }
    };
    loadCreators();
  }, []);

  // Set giá trị mặc định cho nhà máy
  useEffect(() => {
    if (commonStore.commonStore.length && !form.getFieldValue('saleOrgCode')) {
      form.setFieldValue('saleOrgCode', commonStore.commonStore[0].key);
    }
  }, [form, commonStore.commonStore.length]);

  return (
    <>
      <SearchForm
        form={form}
        onFinish={onFinish}
        className="gap-x-12"
        layout="vertical"
        column="col-2"
        initialValues={initSearchFilters}
      >
        <button type="submit" hidden></button>

        {/* Nhà máy */}
        <SearchItem
          label={t('waste-handover-plan.factory')}
          name="saleOrgCode"
          rules={[{ required: true, message: t('message.required-select') }]}
        >
          <Select
            // always required, no clear button
            showSearch
            placeholder={t('place-holder.please-select')}
            fieldNames={{ value: 'key', label: 'value' }}
            options={commonStore.commonStore}
            filterOption={(input, option) =>
              (option?.value?.toLowerCase() ?? '').includes(input.toLowerCase())
            }
          />
        </SearchItem>

        {/* Phân nhóm chất thải/phế liệu (HĐ) - cho chọn nhiều */}
        <SearchItem label={t('masterdata.waste-grouping-contract.title')} name="wasteGroupingContractIds">
          <Select
            allowClear
            mode="multiple"
            showSearch
            placeholder={t('place-holder.please-select')}
            options={wasteGroupOptions}
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </SearchItem>

        {/* Mã chất thải/phế liệu với popup tìm kiếm nhiều */}
        <div className="relative">
          <SearchItem label={t('waste-handover-plan.waste-code-search')} name="productCode">
            <Input
              allowClear
              placeholder={t('place-holder.enter-waste-code')}
              onChange={(e) => {
                if (!e.target.value) {
                  productCodePopupRef.current?.clearData();
                }
              }}
            />
          </SearchItem>
          <div
            onClick={() => setOpenProductCodeModal(true)}
            className="absolute top-1/2 -right-6 cursor-pointer hover:text-blue-500"
          >
            <HiOutlineExternalLink className="text-xl" />
          </div>
        </div>

        {/* Tên chất thải/phế liệu */}
        <SearchItem label={t('waste-handover-plan.waste-name-search')} name="productName">
          <Input allowClear placeholder={t('place-holder.enter-waste-name')} />
        </SearchItem>

        {/* Mã nhà cung cấp với popup tìm kiếm nhiều */}
        <div className="relative">
          <SearchItem label={t('waste-handover-plan.vendor-code-search')}
            name="vendorNumber"
            initialValue={getRole() === "NCC_CT" ? getUserName() : ""}
          >
            <Input
              allowClear
              placeholder={t('place-holder.enter-vendor-code')}
              onChange={(e) => {
                if (!e.target.value) {
                  vendorPopupRef.current?.clearData();
                }
              }}
              disabled={getRole() === "NCC_CT"}
            />
          </SearchItem>
          {/* <div
            onClick={() => setOpenVendorModal(true)}
            className="absolute top-1/2 -right-6 cursor-pointer hover:text-blue-500"
          >
            <HiOutlineExternalLink className="text-xl" />
          </div> */}
          {getRole() !== "NCC_CT" && <div
            onClick={() => {
              setOpenVendorModal(true)
            }}
            className="absolute top-1/2 -right-6 cursor-pointer hover:text-blue-500">
            <HiOutlineExternalLink className="text-xl" />
          </div>}
        </div>

        {/* Tên nhà cung cấp */}
        <SearchItem name={'vendorName'} label={'Tên nhà cung cấp'} initialValue={getRole() === "NCC_CT" ? getFullName() : ""} >
          <Input allowClear placeholder={t('place-holder.enter-vendor-name')} disabled={getRole() === "NCC_CT"} />
        </SearchItem>

        {/* Số biên bản bàn giao */}
        <SearchItem label={t('waste-handover-plan.delivery-number-search')} name="handoverDocumentNumber">
          <Input allowClear placeholder={t('place-holder.enter-delivery-number')} />
        </SearchItem>

        {/* Trạng thái */}
        <SearchItem label={t('waste-handover-plan.status-search')} name="actived">
          <Select allowClear options={dropdownStatus} placeholder={t('place-holder.all')} />
        </SearchItem>

        {/* Ngày bàn giao dự kiến - preset */}
        <SearchItem label={t('waste-handover-plan.delivery-date-preset')} name="deliveryDatePreset">
          <Select
            allowClear
            placeholder={t('place-holder.quick-select')}
            options={commonStore.listCommonDate2}
            fieldNames={{ value: 'key', label: 'value' }}
            onChange={onDeliveryPresetChange}
          />
        </SearchItem>

        {/* Người tạo */}
        <SearchItem label={t('waste-handover-plan.creator')} name="createBy">
          <Select
            allowClear
            mode="multiple"
            showSearch
            placeholder={t('place-holder.please-select')}
            options={creatorOptions}
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </SearchItem>

        {/* Từ ngày */}
        <SearchItem
          label={t('waste-handover-plan.from-date')}
          name="deliveryDateFrom"
          dependencies={['deliveryDateTo', 'deliveryDatePreset']}
          validateTrigger="onSubmit"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const preset = getFieldValue('deliveryDatePreset');
                // Validate khi chọn Custom hoặc khi preset là rỗng/undefined
                if (preset === 'Custom' || !preset) {
                  if (value || getFieldValue('deliveryDateTo')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Chưa nhập giá trị "Từ ngày".'));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker
            className="w-full"
            format={DATE_FORMAT_DEFAULT}
            disabled={disableDeliveryDate}
          />
        </SearchItem>

        {/* Đến ngày */}
        <SearchItem
          label={t('waste-handover-plan.to-date')}
          name="deliveryDateTo"
          dependencies={['deliveryDateFrom', 'deliveryDatePreset']}
          validateTrigger="onSubmit"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const preset = getFieldValue('deliveryDatePreset');
                // Validate khi chọn Custom hoặc khi preset là rỗng/undefined
                if (preset === 'Custom' || !preset) {
                  if (value || getFieldValue('deliveryDateFrom')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Chưa nhập giá trị "Đến ngày".'));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker
            className="w-full"
            format={DATE_FORMAT_DEFAULT}
            disabled={disableDeliveryDate}
          />
        </SearchItem>
      </SearchForm>

      {/* Popup tìm kiếm nhiều mã chất thải/phế liệu */}
      <SearchMultiplePopup
        ref={productCodePopupRef}
        title={t('waste-handover-plan.waste-code-search')}
        open={openProductCodeModal}
        onOpenChange={setOpenProductCodeModal}
        onOk={(value) => {
          form.setFieldValue('productCode', value);
        }}
        onReset={() => {
          form.setFieldValue('productCode', undefined);
        }}
      />

      {/* Popup tìm kiếm nhiều mã nhà cung cấp */}
      <SearchMultiplePopup
        ref={vendorPopupRef}
        title={t('waste-handover-plan.vendor-code-search')}
        open={openVendorModal}
        onOpenChange={setOpenVendorModal}
        onOk={(value) => {
          form.setFieldValue('vendorNumber', value);
        }}
        onReset={() => {
          form.setFieldValue('vendorNumber', undefined);
        }}
      />
    </>
  );
};

